import { assetUrl } from './WebsiteContext';
import React, { createContext, useContext, useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'kyis_smart_session';
const INTENTS_KEY = 'kyis_intents';

/**
 * Intent half-life in hours. After this many hours of inactivity, a signal
 * drops to 50% weight. After 2× → 25%, 3× → 12.5%, etc. This biases
 * personalization toward recent behaviour without forgetting prior sessions.
 *
 * 72h (3 days) means same-session signals stay strong, next-day signals stay
 * meaningful, week-old signals fade to ~10%, and month-old signals are ~1%.
 */
const INTENT_HALFLIFE_HOURS = 72;

/** Signal weights by event type. Higher = stronger intent indicator. */
const SIGNAL_WEIGHTS = {
  service_click: 4,
  cta_click: 3,
  faq_click: 2,
  section_view: 1.5,
  page_view: 1,
};

/** Migrate legacy numeric intents → timestamped format (best-effort). */
const loadRawSignals = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(INTENTS_KEY) || '{}');
    const now = Date.now();
    const migrated = {};
    for (const [cat, val] of Object.entries(raw)) {
      if (typeof val === 'number') {
        // Legacy format — backdate slightly so it still contributes briefly then fades
        migrated[cat] = { w: val, ts: now - 24 * 3600 * 1000 };
      } else if (val && typeof val.w === 'number' && typeof val.ts === 'number') {
        migrated[cat] = val;
      }
    }
    return migrated;
  } catch {
    return {};
  }
};

/** Compute decayed weight for a given raw signal at the current moment. */
export const decayedWeight = ({ w, ts }, now = Date.now()) => {
  if (!w || !ts) return 0;
  const hours = (now - ts) / (3600 * 1000);
  if (hours <= 0) return w;
  return w * Math.pow(0.5, hours / INTENT_HALFLIFE_HOURS);
};

/** Produce the decayed intents map consumers use to pick topIntent. */
export const decayIntents = (rawSignals, now = Date.now()) => {
  const out = {};
  for (const [cat, sig] of Object.entries(rawSignals || {})) {
    out[cat] = decayedWeight(sig, now);
  }
  return out;
};

const SmartSiteContext = createContext(null);

export const useSmartSite = () => useContext(SmartSiteContext);

/**
 * SmartSiteProvider
 * - Manages a persistent session ID (localStorage)
 * - Automatically tracks page views
 * - Provides track() for custom events
 * - Provides useSmartContent(surface) for adaptive content fetching
 */
export const SmartSiteProvider = ({ children }) => {
  const [sessionId, setSessionId] = useState(null);
  const [summary, setSummary] = useState(null);
  // Raw signals: { [category]: { w: weight, ts: epochMs } }
  // We store the *undecayed* weight + timestamp and compute decay at read time.
  const [rawSignals, setRawSignals] = useState(loadRawSignals);
  const initialized = useRef(false);
  const location = useLocation();
  const trackQueue = useRef([]);

  // Initialize session
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    initSession();
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (!sessionId) return;
    track('page_view', { page: location.pathname });
  }, [location.pathname, sessionId]);

  const initSession = async () => {
    const stored = localStorage.getItem(SESSION_KEY);
    try {
      const res = await axios.post(`${API_URL}/api/smart/session`, {
        session_id: stored || undefined,
        user_agent: navigator.userAgent,
      });
      const sid = res.data.session_id;
      localStorage.setItem(SESSION_KEY, sid);
      setSessionId(sid);
      // Flush queued events
      for (const evt of trackQueue.current) {
        sendEvent(sid, evt);
      }
      trackQueue.current = [];
    } catch (err) {
      console.error('Smart session init failed:', err);
      // Generate a local fallback ID
      const fallback = stored || crypto.randomUUID();
      localStorage.setItem(SESSION_KEY, fallback);
      setSessionId(fallback);
    }
  };

  const sendEvent = async (sid, eventData) => {
    try {
      await axios.post(`${API_URL}/api/smart/track`, { session_id: sid, ...eventData });
    } catch {}
  };

  const track = useCallback((eventType, data = {}) => {
    const evt = { event_type: eventType, ...data };
    if (!sessionId) {
      trackQueue.current.push(evt);
      return;
    }
    sendEvent(sessionId, evt);
    // Update local signals in real-time for immediate UI reactivity.
    // Neutral categories (e.g. 'general') are NOT tracked as intent signals —
    // they apply to every customer type and would pollute top-intent detection.
    if (data.content_category && data.content_category !== 'general') {
      const w = SIGNAL_WEIGHTS[eventType] || 1;
      setRawSignals(prev => {
        // Accumulate within the same category: decay the prior weight to "now",
        // then add the fresh signal. This way repeated clicks compound
        // correctly while still biasing toward recent activity across categories.
        const now = Date.now();
        const prior = prev[data.content_category];
        const decayedPrior = prior ? decayedWeight(prior, now) : 0;
        const updated = {
          ...prev,
          [data.content_category]: { w: decayedPrior + w, ts: now },
        };
        localStorage.setItem(INTENTS_KEY, JSON.stringify(updated));
        return updated;
      });
    }
  }, [sessionId]);

  const refreshSummary = useCallback(async () => {
    if (!sessionId) return null;
    try {
      const res = await axios.get(`${API_URL}/api/smart/session/${sessionId}/summary`);
      setSummary(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, [sessionId]);

  // Derive decayed-intents map on every render so consumers always see
  // time-weighted values (recent signals beat older ones automatically).
  const intents = useMemo(() => decayIntents(rawSignals), [rawSignals]);

  return (
    <SmartSiteContext.Provider value={{ sessionId, track, summary, refreshSummary, intents, rawSignals }}>
      {children}
    </SmartSiteContext.Provider>
  );
};

/**
 * useSmartContent(surfaceSlug, contentType?)
 * Fetches adaptive content for a given surface, optionally personalized by session.
 * Returns { items, personalized, loading }
 */
export function useSmartContent(surfaceSlug, contentType) {
  const [items, setItems] = useState([]);
  const [personalized, setPersonalized] = useState(false);
  const [loading, setLoading] = useState(true);
  const smart = useSmartSite();

  useEffect(() => {
    if (!surfaceSlug) return;
    // Delay smart content fetches until the sessionId is known so
    // the initial HTTP/1.1 connection pool isn't saturated on first paint.
    if (!smart?.sessionId) return;
    fetchContent();
  }, [surfaceSlug, smart?.sessionId]);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const params = {};
      if (smart?.sessionId) params.session_id = smart.sessionId;
      if (contentType) params.content_type = contentType;
      const res = await axios.get(`${API_URL}/api/smart/content/${surfaceSlug}`, { params });
      setItems((res.data.items || []).map(item => { const d=item.data || {}; return {...item, data:{...d,...(d.image?{image:assetUrl(d.image)}:{}), ...(d.headline?{heading:d.headline}:{}),...(d.button_text?{cta_text:d.button_text}:{}),...(d.button_link?{cta_link:d.button_link}:{}),...(d.secondary_link?{secondary_cta_link:d.secondary_link}:{}),...(d.secondary_text?{secondary_cta_text:d.secondary_text}:{})}}; }));
      setPersonalized(res.data.personalized || false);
    } catch {
      setItems([]);
      setPersonalized(false);
    } finally {
      setLoading(false);
    }
  };

  return { items, personalized, loading, refetch: fetchContent };
}
